/* global globalThis */
import { useState, useEffect, useRef } from "react";
import "./Header.css";
import { useMsal } from "@azure/msal-react";
import { AppBar, Avatar, Box, IconButton, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { togglePhi } from "../../../redux/slices/togglesSlice";
import { logout } from "../../../redux/slices/authSlice";
import { persistor } from "../../../redux/store";
import ToggleButton from "../toggleButton/toggleButton";

import TopNavigation from "../navigation/TopNavigation";
import { AppLogo } from "../navigation/Navigation";

const Header = ({ drawerWidth = 0, handleDrawerToggle, hideSearch }) => {
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const { instance } = useMsal();
  const photo = localStorage.getItem("profilePhoto");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const profileRef = useRef(null);
  const dispatch = useDispatch();
  const isPhiMaskingOn = useSelector((state) => state.toggles.phi);
  const handleImageError = () => {
    setImageError(true);
  };

  const handleProfileImageError = () => {
    setProfileImageError(true);
  };

  const handleLogout = () => {
    const accounts = instance.getAllAccounts();
    const activeAccount = instance.getActiveAccount();
    const account = accounts?.[0] || activeAccount;

    const logoutRequest = {
      account: account || undefined,
      postLogoutRedirectUri: `/landing`,
      // Clear storage only at the moment of redirect so the app doesn't flash /landing first
      onRedirectNavigate: () => {
        // Make browser Back from the accounts page land on /landing
        globalThis.history.replaceState(null, "", "/landing");
        persistor.purge();
        localStorage.clear();
        sessionStorage.clear();
        return true;
      },
    };

    instance.logoutRedirect(logoutRequest).catch((error) => {
      console.warn("Logout redirect failed:", error);
      dispatch(logout());
      globalThis.location.href = `/landing`;
    });
  };

  const stringAvatar = (name = "") => {
    const parts = name.trim().split(" ");
    const initials =
      (parts[0]?.[0] || "").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
    return {
      sx: {
        bgcolor: "rgba(255, 255, 255, 0.22)",
        color: "#FFFFFF",
        width: 30,
        height: 30,
        fontWeight: 700,
        fontSize: "12px",
        border: "1.5px solid rgba(255, 255, 255, 0.6)",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          bgcolor: "rgba(255, 255, 255, 0.35)",
          border: "1.5px solid #FFFFFF",
        },
      },
      children: initials || "?",
    };
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileCard(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const userFullName = `${userDetails?.firstName || ""} ${
    userDetails?.lastName || ""
  }`.trim();
  return (
    <AppBar
      position="fixed"
      sx={{
        background: "#403C6F",
        boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
        width: "100%",
        ml: 0,
        left: 0,
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 1, sm: 1.5, md: 2 },
          height: { xs: 40, sm: 42 },
          width: "100%",
        }}
      >
        {/* Left Section: App Logo & Top Horizontal Navigation */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            flex: 1,
            overflow: "hidden",
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 1,
              display: { md: "none" },
              color: "#FFFFFF",
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              mr: { xs: 0.5, sm: 1 },
              flexShrink: 0,
            }}
          >
            <AppLogo width="65" height="26" />
          </Box>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              height: "100%",
              flex: 1,
              overflow: "hidden",
            }}
          >
            <TopNavigation />
          </Box>
        </Box>
        <Box
          className="header-right"
          ref={profileRef}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5, md: 2 },
            flexShrink: 0,
            ml: "auto",
          }}
        >
          <Box
            data-phi-toggle="true"
            className="phi-toggle-container"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "4px", sm: "6px" },
              bgcolor: "transparent",
              color: "#FFFFFF",
              textTransform: "none",
              padding: 0,
              minWidth: 0,
              height: 22,
              px: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "11px", sm: "13px" },
                fontWeight: isPhiMaskingOn ? 600 : 500,
                color: "#FFFFFF",
                whiteSpace: "nowrap",
              }}
            >
              Mask PHI
            </Typography>
            <ToggleButton
              isOn={isPhiMaskingOn}
              onChange={() => dispatch(togglePhi())}
              label=""
              onLabel=""
              offLabel=""
              sx={{
                bgcolor: "transparent",
                color: "#FFFFFF",
                padding: 0,
                minWidth: 0,
                height: 24,
                gap: 0,
                px: 0,
                "&:hover": { bgcolor: "transparent" },
                "& > div": {
                  border: isPhiMaskingOn ? "1.5px solid #FFFFFF" : "none",
                },
              }}
            />
          </Box>
          <Box
            className="profile"
            onClick={() => setShowProfileCard((prev) => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              position: "relative",
            }}
          >
            {photo && !imageError ? (
              <img
                src={photo}
                alt="User"
                onError={handleImageError}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1.5px solid rgba(255, 255, 255, 0.6)",
                }}
              />
            ) : (
              <Avatar {...stringAvatar(userFullName)} />
            )}
          </Box>
          {showProfileCard && (
            <Box
              className="profile-card"
              sx={{
                position: "absolute",
                right: { xs: "10px", sm: 0 },
                top: 54,
                backgroundColor: "#F7F8FB",
                borderRadius: "12px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                p: 2,
                minWidth: { xs: "300px", sm: 380 },
                maxWidth: { xs: "calc(100vw - 20px)", sm: 450 },
                width: { xs: "90vw", sm: "max-content" },
                zIndex: 1500,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  gap: 2,
                  minHeight: 80,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {photo && !profileImageError ? (
                    <img
                      src={photo}
                      alt="Profile"
                      onError={handleProfileImageError}
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        objectFit: "cover",
                        marginRight: 12,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Avatar
                      {...stringAvatar(userFullName)}
                      sx={{
                        width: 48,
                        height: 48,
                        mr: 1.5,
                        bgcolor: "#403c6f",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0, maxWidth: 280 }}>
                    <Box
                      sx={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: "#2E2E2E",
                        lineHeight: 1.3,
                      }}
                    >
                      {userFullName || "User"}
                    </Box>
                    <Box sx={{ fontSize: 14, color: "#6B7280", mt: 0.5 }}>
                      User Role - {userDetails?.primaryRoleName || "N/A"}
                    </Box>
                    <Box sx={{ fontSize: 13, color: "#6B7280", mt: 0.5 }}>
                      {userDetails?.emailId || userDetails?.email || ""}
                    </Box>
                  </Box>
                </Box>
                <button
                  onClick={handleLogout}
                  style={{
                    backgroundColor: "#fff",
                    color: "#f44336",
                    border: "1px solid #f44336",
                    borderRadius: "8px",
                    padding: "10px 16px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "0.2s",
                    height: "40px",
                    minWidth: "80px",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#fff5f5")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "#fff")
                  }
                  onFocus={(e) =>
                    (e.currentTarget.style.backgroundColor = "#fff5f5")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.backgroundColor = "#fff")
                  }
                >
                  Logout
                </button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </AppBar>
  );
};

Header.propTypes = {
  drawerWidth: PropTypes.number,
  handleDrawerToggle: PropTypes.func,
  hideSearch: PropTypes.bool,
};

export default Header;
